import classnames from "classnames";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useFetchDataImmutable } from "../../apiHelpers";
import { API_ROUTES } from "../../constants";
import ActiveComponentContext from "../../contexts/ActiveComponentContext";
import { appendParamsToUrl } from "../../helpers";
import useOutputReportStore from "../../store/OutputReport.store";
import { AVAILABLE_SECTIONS_LIST_PER_REPORT } from "../../utils/helpers/customizationLabels.js";
import CashCorporateReport from "./CashCorporateReport";
import CashEquityReport from "./CashEquityReport";
import CashMuniReport from "./CashMuniReport";
import CashTreasuryBillReport from "./CashTreasuryBillReport";
import CashTreasuryReport from "./CashTreasuryReport";
import TransitionCorporateReport from "./TransitionCorporateReport";
import TransitionEquityReport from "./TransitionEquityReport";
import TransitionMuniReport from "./TransitionMuniReport";
import TransitionTreasuryReport from "./TransitionTreasuryReport";

import { processAllDataFromSections } from "../../utils/helpers/reportData.service.js";

import "./PrintReport.scss";
import "./Report.scss";

const getReportType = (data) => {
  const reportType = data.Cover["Report Type"]?.toLowerCase();
  const assetType = data?.Cover?.["Asset Type"]?.toLowerCase();
  return { reportType, assetType };
};

const ReportPageContainer = ({
  reportType,
  assetType,
  allPossibleSections,
}) => {
  const [activeComponent, setActiveComponent] = useState(null);
  const contextValue = {
    active: activeComponent,
    setActive: setActiveComponent,
  };

  if (!reportType && !assetType)
    return (
      <ActiveComponentContext.Provider value={contextValue}>
        <div className={classnames(reportType, assetType)}>
          <TransitionMuniReport />
        </div>
      </ActiveComponentContext.Provider>
    );

  const templates = {
    cash: {
      muni: <CashMuniReport />,
      corp: <CashCorporateReport />,
      treasury: <CashTreasuryReport />,
      tbill: <CashTreasuryBillReport />,
      equity: <CashEquityReport />,
    },
    transition: {
      muni: <TransitionMuniReport />,
      corp: <TransitionCorporateReport />,
      treasury: <TransitionTreasuryReport />,
      equity: <TransitionEquityReport />,
    },
  };

  return (
    <ActiveComponentContext.Provider value={contextValue}>
      <div className={classnames(reportType, assetType)}>
        {templates[reportType]?.[assetType]}
      </div>
    </ActiveComponentContext.Provider>
  );
};

ReportPageContainer.propTypes = {
  reportType: PropTypes.oneOf(["transition", "cash"]),
  assetType: PropTypes.oneOf(["corp", "muni", "treasury", "tbill", "equity"]),
};

// @NOTE: props are just temporary so we can display in
// Storybook the different kinds of report pages
const Report = () => {
  const { id: transitionId } = useParams();
  const navigate = useNavigate();
  // This errors out on back end and needs to be fixed
  const reportUrl = appendParamsToUrl(API_ROUTES.OUTPUT_REPORT_DETAIL, {
    transitionId,
  });

  // Incompatible data format from service

  const { data: fetchedProposalDetails } = useFetchDataImmutable(
    {
      url: appendParamsToUrl(API_ROUTES.PROPOSAL_DETAIL, {
        transitionId: transitionId,
      }),
    },
    !!transitionId
  );

  const mockData = {
    sections: [
      "Firm Overview",
      "1-Sheeter Summary",
      "Proposed Portfolio",
      // ...
    ],
    cover: {
      Client: "",
      Advisor: "",
    },
    displayCustomizations: true,
    mockedData: true,
  };
  //! Get this working
  //useMetadata(transitionId, mockData);

  const {
    actions: { handleSetReportData, handleSetDataTypes, handleSetMetadata },
    data,
  } = useOutputReportStore();

  const { data: reportData, error: reportError } = useFetchDataImmutable({
    url: reportUrl,
  });

  //! Uncomment once AllSpring update the endpoint
  const { data: formatTypes } = useFetchDataImmutable({
    url: API_ROUTES.OUTPUT_REPORT_FORMATTING,
  });
  //! Delete once AllSpring update the endpoint
  // const formatTypes = standardizedTypes;

  // const outputType = getOutputType(reportData);

  // useEffect(() => {
  //   if (reportData && fetchedProposalDetails) {
  //     reportData.Cover.QuestionnaireId =
  //       fetchedProposalDetails?.questionnaireId || null;
  //     reportData.proposalDetails = fetchedProposalDetails;
  //     handleSetReportData(reportData);
  //   }
  // }, [reportData, handleSetReportData, fetchedProposalDetails]);

  useEffect(() => {
    if (formatTypes) {
      handleSetDataTypes(formatTypes);
    }
  }, [formatTypes, handleSetDataTypes]);

  useEffect(() => {
    if (reportError?.status) {
      navigate(`/404`);
    }
  }, [reportError, navigate]);

  useEffect(() => {
    if (reportData) {
      handleSetReportData(reportData);
      //handleSetMetadata(reportData);
      const { assetType, reportType } = getReportType(reportData);

      const availableSections =
        AVAILABLE_SECTIONS_LIST_PER_REPORT[reportType][assetType];
      const allSectionsMetadata = processAllDataFromSections(reportData, [
        "Cover",
        ...availableSections,
      ]);
      handleSetMetadata(allSectionsMetadata);
    }
  }, [handleSetMetadata, handleSetReportData, reportData]);

  if (!reportData) return null;

  if (reportData) {
    const { assetType, reportType } = getReportType(reportData);
    return (
      <section className="report">
        <ReportPageContainer
          assetType={assetType}
          reportType={reportType}
          allPossibleSections={AVAILABLE_SECTIONS_LIST_PER_REPORT}
        />
      </section>
    );
  }
};

Report.propTypes = {
  // reportType: PropTypes.oneOf(["transition", "cash"]),
  // assetType: PropTypes.oneOf(["corp", "muni", "treasury", "tbill", "equity"]),
};

export default Report;
